// ============================================================================
// AXIOS INTERCEPTORS
// ============================================================================
// Request and Response interceptors for axios instance

import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig, tokenUtils, HTTP_STATUS } from '../config/apiConfig';
import { authEndpoints } from '../endpoints';
import { logRequest, logResponse, logError, logWarning } from './logger';

/**
 * Setup Request Interceptor
 * - Adds authentication token to headers
 * - Adds request timestamp for performance tracking
 * - Logs request details
 */
export const setupRequestInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add timestamp for performance tracking
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Check if this is an auth endpoint (should not send existing token)
      const requestUrl = config.url?.toLowerCase() || '';
      const isAuthEndpoint = requestUrl.includes('/auth/discover') ||
                             requestUrl.includes('/auth/authenticate') ||
                             requestUrl.includes('/auth/login') ||
                             requestUrl.includes('/auth/refresh-hub');

      // Add authentication token if available (skip for auth endpoints to avoid invalid token errors)
      if (!isAuthEndpoint) {
        const token = tokenUtils.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Add common headers
      // For FormData: DELETE Content-Type to let browser/axios set it automatically with boundary
      // (axios.create sets a default Content-Type: application/json which breaks FormData uploads)
      const isFormData = config.data instanceof FormData;
      if (isFormData) {
        // Remove Content-Type to allow axios to set multipart/form-data with proper boundary
        delete config.headers['Content-Type'];
      } else {
        config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      }
      config.headers['Accept'] = 'application/json';

      // Add required multi-tenant headers from user context (skip for auth endpoints)
      // User ID, Tenant ID, and App ID are set in sessionStorage during login (see authService.authenticate)
      if (!isAuthEndpoint) {
        const userId = tokenUtils.getUserId();
        const tenantId = tokenUtils.getTenantId();
        const appId = tokenUtils.getAppId();

        // Only add headers if values exist (from login session)
        // Priority: 1) Header override, 2) SessionStorage (from login)
        const effectiveUserId = config.headers['X-User-ID'] || userId;
        const effectiveTenantId = config.headers['X-Tenant-ID'] || tenantId;
        const effectiveAppId = config.headers['X-App-ID'] || appId;

        // Set headers only if we have valid values
        if (effectiveUserId) {
          config.headers['X-User-ID'] = effectiveUserId;
        }
        if (effectiveTenantId) {
          config.headers['X-Tenant-ID'] = effectiveTenantId;
        }
        if (effectiveAppId) {
          config.headers['X-App-ID'] = effectiveAppId;
        }
      }

      // Log request
      logRequest(config);

      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );
};

/**
 * Setup Response Interceptor
 * - Logs response details
 * - Handles successful responses
 * - Processes response data
 */
export const setupResponseInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log response
      logResponse(response);

      // Return response data directly for easier consumption
      return response;
    },
    async (error: AxiosError) => {
      // Log error
      logError(error);

      // Handle specific error cases
      if (error.response) {
        const { status } = error.response;

        // Handle 401 Unauthorized - Token expired or invalid
        // Skip redirect for auth endpoints - let the component handle auth failures
        const requestUrl = error.config?.url?.toLowerCase() || '';
        const isAuthRequest = requestUrl.includes('/auth/login') ||
                              requestUrl.includes('/auth/me') ||
                              requestUrl.includes('/auth/refresh') ||
                              requestUrl.includes('/auth/refresh-hub') ||
                              requestUrl.includes('/auth/discover') ||
                              requestUrl.includes('/auth/authenticate');
        if (status === HTTP_STATUS.UNAUTHORIZED && !isAuthRequest) {
          // Return the result so successful retry propagates back to the caller
          // Without return, the code falls through to Promise.reject(error) at the end
          return handleUnauthorizedError(axiosInstance, error);
        }

        // Handle 403 Forbidden - Insufficient permissions
        if (status === HTTP_STATUS.FORBIDDEN) {
          handleForbiddenError();
          const forbiddenError = new Error(
            (error.response?.data as any)?.message ||
            'You do not have permission to perform this action. Please contact your administrator.'
          );
          return Promise.reject(forbiddenError);
        }

        // Handle 404 Not Found
        if (status === HTTP_STATUS.NOT_FOUND) {
          handleNotFoundError(error);
        }

        // Handle 500 Internal Server Error
        if (status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          handleServerError();
        }

        // Extract API error message from response body if available
        // Supports both ApiResponse format and ASP.NET ValidationProblemDetails format
        const responseData = error.response?.data as any;
        if (responseData?.message) {
          // Standard ApiResponse: { message: "...", errors: [...] }
          error.message = responseData.message;
        } else if (responseData?.title) {
          // ASP.NET ValidationProblemDetails: { title: "...", errors: { field: ["msg"] } }
          const validationErrors = responseData.errors;
          if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
            // Extract field-level validation error messages
            const messages = Object.entries(validationErrors)
              .flatMap(([, msgs]) => msgs as string[]);
            error.message = messages.length > 0 ? messages.join('. ') : responseData.title;
          } else {
            error.message = responseData.title;
          }
        } else if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
          // ApiResponse with errors array: { errors: ["msg1", "msg2"] }
          error.message = responseData.errors.join('. ');
        }
      } else if (error.request) {
        // Request was made but no response received (network error)
        handleNetworkError();
        error.message = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened while setting up the request
        handleUnknownError(error);
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Handle 401 Unauthorized Error
 * - Attempts to refresh token
 * - If refresh fails, redirects to login
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

async function handleUnauthorizedError(
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<any> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  console.log('[TokenRefresh] 401 detected, attempting token refresh...');

  // Avoid infinite loops
  if (originalRequest._retry) {
    console.log('[TokenRefresh] Already retried, redirecting to login');
    logWarning('Token refresh failed, redirecting to login');
    tokenUtils.clearTokens();
    window.location.href = '/login';
    return;
  }

  // If already refreshing, queue this request
  if (isRefreshing) {
    console.log('[TokenRefresh] Already refreshing, queuing request');
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = tokenUtils.getRefreshToken();
  const tenantId = tokenUtils.getTenantId();
  const applicationId = tokenUtils.getAppId();

  console.log('[TokenRefresh] Refresh token available:', !!refreshToken);
  console.log('[TokenRefresh] Tenant ID available:', !!tenantId, tenantId);
  console.log('[TokenRefresh] Application ID available:', !!applicationId, applicationId);

  if (!refreshToken) {
    console.log('[TokenRefresh] No refresh token, redirecting to login');
    logWarning('No refresh token available, redirecting to login');
    isRefreshing = false;
    tokenUtils.clearTokens();
    window.location.href = '/login';
    return;
  }

  if (!tenantId) {
    console.log('[TokenRefresh] No tenant ID, redirecting to login');
    logWarning('No tenant ID available for token refresh, redirecting to login');
    isRefreshing = false;
    tokenUtils.clearTokens();
    window.location.href = '/login';
    return;
  }

  if (!applicationId) {
    console.log('[TokenRefresh] No application ID, redirecting to login');
    logWarning('No application ID available for token refresh, redirecting to login');
    isRefreshing = false;
    tokenUtils.clearTokens();
    window.location.href = '/login';
    return;
  }

  try {
    // Use authEndpoints for consistent URL generation
    const refreshUrl = authEndpoints.refreshHub();
    console.log('[TokenRefresh] Calling', refreshUrl);
    // Attempt to refresh token via Tenant Hub endpoint with dynamic client credentials
    const response = await axiosInstance.post(refreshUrl, {
      refreshToken,
      tenantId,
      applicationId,
    });

    console.log('[TokenRefresh] Refresh response:', response.data);

    // Handle Tenant Hub response format (ApiResponse<AuthenticateResponse>)
    const responseData = response.data;
    const authData = responseData.success ? responseData.data : responseData;

    const newToken = authData.accessToken;
    const newRefreshToken = authData.refreshToken;

    if (!newToken) {
      throw new Error('No access token in refresh response');
    }

    console.log('[TokenRefresh] New tokens received, storing...');

    // Store new tokens
    tokenUtils.setToken(newToken);
    if (newRefreshToken) {
      tokenUtils.setRefreshToken(newRefreshToken);
    }

    // Update authorization header
    originalRequest.headers.Authorization = `Bearer ${newToken}`;

    // Process queued requests
    processQueue(null, newToken);

    console.log('[TokenRefresh] Retrying original request...');
    // Retry original request
    return axiosInstance(originalRequest);
  } catch (refreshError: any) {
    // Refresh failed, clear tokens and redirect to login
    console.error('[TokenRefresh] Refresh failed:', refreshError?.response?.data || refreshError?.message);
    processQueue(refreshError, null);
    tokenUtils.clearTokens();
    window.location.href = '/login';
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

/**
 * Handle 403 Forbidden Error
 */
function handleForbiddenError(): void {
  logWarning('Access forbidden - Insufficient permissions');
  // Optional: Show notification to user
  // notificationService.error('You do not have permission to perform this action');
}

/**
 * Handle 404 Not Found Error
 */
function handleNotFoundError(error: AxiosError): void {
  logWarning('Resource not found', error.config?.url);
  // Optional: Show notification to user
  // notificationService.warning('Requested resource not found');
}

/**
 * Handle 500 Internal Server Error
 */
function handleServerError(): void {
  logWarning('Internal server error');
  // Optional: Show notification to user
  // notificationService.error('Server error. Please try again later');
}

/**
 * Handle Network Error
 */
function handleNetworkError(): void {
  logWarning('Network error - No response from server');
  // Optional: Show notification to user
  // notificationService.error('Network error. Please check your connection');
}

/**
 * Handle Unknown Error
 */
function handleUnknownError(error: AxiosError): void {
  logWarning('Unknown error occurred', error.message);
  // Optional: Show notification to user
  // notificationService.error('An unexpected error occurred');
}
